import type { RefCallback } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import { calculateTrackingInView, handleSkipInView } from 'components/utils/OfferInView'

interface UseOfferInViewTrackingOptions {
  rootMargin?: string
  threshold?: Array<number>
}

interface UseOfferInViewTrackingReturn {
  setTileRef: RefCallback<HTMLDivElement>
  trackingInView: boolean | undefined
  tileRef: React.RefObject<HTMLDivElement | null>
}

export function useOfferInViewTracking(
  options: UseOfferInViewTrackingOptions = {},
): UseOfferInViewTrackingReturn {
  const { rootMargin = '100px', threshold = [0, OFFER_TRACKING_IN_VIEW_THRESHOLD] } = options

  const [skipInView, setSkipInView] = useState(false)
  const [tileInViewRef, tileInView, entry] = useInView({
    rootMargin,
    threshold,
    skip: skipInView,
  })
  const tileRef = useRef<HTMLDivElement>(null)

  const setTileRef = useCallback<RefCallback<HTMLDivElement>>((instance) => {
    tileInViewRef(instance)
    tileRef.current = instance
  }, [tileInViewRef])

  useEffect(() => {
    handleSkipInView(entry, skipInView, setSkipInView)
  }, [entry, skipInView, setSkipInView, tileInView])

  const trackingInView = useMemo(() => calculateTrackingInView(entry), [entry])

  return {
    setTileRef,
    trackingInView,
    tileRef,
  }
}
